/**
 * @file TradierAccess.jsx
 * @description Handles Tradier brokerage platform connection and authentication
 *
 * @section Responsibilities
 * - Manage Tradier API connection
 * - Handle authentication flow
 * - Process authorization tokens
 * - Manage connection state
 * - Handle error states
 * - Provide connection status
 *
 * @section Dependencies
 * - TradierAPI: Brokerage platform API
 * - AuthService: Authentication handling
 * - TokenService: Token management
 * - StateManagement: Connection state
 *
 * @section Integration Points
 * - Tradier API: Platform connection
 * - Auth System: User authentication
 * - Token System: Authorization tokens
 * - Navigation System: Post-connection routing
 *
 * @section State Management
 * - connectionState: Connection status
 * - authState: Authentication status
 * - tokenState: Token management
 * - errorState: Error handling
 *
 * @section Events
 * - onConnect: Connection initiation
 * - onAuth: Authentication handling
 * - onToken: Token processing
 * - onError: Error handling
 *
 * @section Props
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialConfig - Initial connection config
 * @prop {Boolean} autoConnect - Controls automatic connection
 * @prop {Object} credentials - Connection credentials
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import Request from "../../utils/fetchUtils";
import { getSessionObject } from "../../utils/sessionUtils";
import Swal from "sweetalert2";
import DgButton from "../../components/DgButton";
import { useQuery } from "../../utils/useQuery";

const TradierAccess = (props) => {
  const query = useQuery();
  const navigate = useNavigate();
  const processAuthCode = async (code) => {
    const { email, sub, role } = getSessionObject();
    const res = await Request(
      "/trading/access",
      { code, email, userId: sub, role },
      "POST"
    );
    if (res.ok) {
      const session = { ...getSessionObject() };
      session.brokerAccounts = res.data.brokerAccounts;
      localStorage.session = JSON.stringify(session);
      Swal.fire(
        "Token successfully generated",
        "Now you can fetch your account data",
        "success"
      ).then(() => {
        navigate("/dashboard");
      });
    }
  };
  const generateAuthorizeUrl = () => {
    const scope = "read,trade,market";
    const { REACT_APP_TRADIER_CLIENT_ID, REACT_APP_TRADIER_CLIENT_SC } =
      process.env;
    return `https://api.tradier.com/v1/oauth/authorize?client_id=${REACT_APP_TRADIER_CLIENT_ID}&scope=${scope}&state=${REACT_APP_TRADIER_CLIENT_SC}`;
  };

  const navigateTradier = () => {
    const newWindow = window.open(generateAuthorizeUrl(), "_blank");
    newWindow.focus();
  };

  useEffect(() => {
    const code = query.get("code");
    if (code) {
      processAuthCode(code);
    }
  }, []);

  return (
    <div>
      <DgButton
        category={"primary"}
        size={"medium"}
        text={"Access Tradier"}
        action={navigateTradier}
      />
    </div>
  );
};

TradierAccess.propTypes = {};

export default TradierAccess;
